import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Card, Select, TextField, Button, ButtonGroup, Banner, EmptyState, BlockStack, Text, ChoiceList, Modal, Checkbox, Tooltip } from '@shopify/polaris';
import { useAuth, useAuthFetch } from './AuthComponent';
import { STORE_KEYS, STORE_DISPLAY_NAMES } from './StoreContext';

import { hasRole } from './constants/roles';
/**
 * Settings → Notifications tab.
 *
 * Two scopes:
 *  - Club: per (store, location, club). Fires for every order at that loc
 *    from that club.
 *  - Order: per (store, order_id). Fires for that one order only. Useful for
 *    one-off corrections.
 *
 * Admin/SM/franchise. Franchise users can only target their accessible
 * locations / orders at their accessible locations.
 */
export default function NotificationsTab() {
    const { user } = useAuth();
    const fetchAuth = useAuthFetch();

    // Cascade-sticky support: the composer card stays pinned at top while the
    // operator browses the table below it. Active heading + thead must cascade
    // BELOW the composer, but composer height varies (~240px order scope vs
    // ~360px club scope). Measure it and feed to a CSS variable; the CSS uses
    // calc(124px + var(--composer-h)) for the next sticky's top.
    const composerRef = useRef(null);
    useEffect(() => {
        const el = composerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const update = () => {
            document.documentElement.style.setProperty('--notif-composer-h', `${el.offsetHeight}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => { ro.disconnect(); document.documentElement.style.removeProperty('--notif-composer-h'); };
    }, []);
    // Same pattern for the Active Notifications heading — its rendered height
    // (~28px) is smaller than the prior hard-coded 32px assumption, leaving a
    // 4px gap above the thead through which rows visibly leak during scroll.
    const activeHeaderRef = useRef(null);
    useEffect(() => {
        const el = activeHeaderRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const update = () => {
            document.documentElement.style.setProperty('--notif-active-header-h', `${el.offsetHeight}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => { ro.disconnect(); document.documentElement.style.removeProperty('--notif-active-header-h'); };
    }, []);
    const allLocations = user?.locations || [];
    const isFranchise = hasRole(user, ['franchise']);
    // Only the platform admin role gets the unlimited cross-store view.
    // site_manager and customer_service operate at specific locations and
    // their Notifications form should pre-select their store the same way
    // a franchise user's does. Backend `_user_can_write_loc` still bypasses
    // for those roles — this only affects the UI dropdowns.
    const isAdmin = hasRole(user, ['admin']);

    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fulfilled-row treatment: 'show' = render as-is; 'hide' = remove rows from
    // the table; 'highlight' = keep visible but apply a pastel-yellow row bg.
    // Only applies to Order-scope rows (single + bulk-all-closed). Club-scope
    // rows have is_fulfilled=null and always render normally.
    const [fulfilledMode, setFulfilledMode] = useState('show');

    // Sort state — initialized to the natural Updated-DESC order returned by
    // the backend so first paint matches what was there before sorting existed.
    const [sortKey, setSortKey] = useState('updated_at');
    const [sortDir, setSortDir] = useState('desc');

    // Multi-select state for bulk delete. selectedIds keys on notification id.
    // lastClickedId remembers the anchor so shift-clicking a row selects the
    // range between the anchor and the new click (mirroring native list
    // multi-select semantics).
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [lastClickedId, setLastClickedId] = useState(null);

    // Native checkbox onChange doesn't expose shiftKey, so track it via a
    // window-level mousedown listener that mirrors into a ref the click handler
    // can read synchronously.
    const shiftHeldRef = useRef(false);
    useEffect(() => {
        const update = (e) => { shiftHeldRef.current = !!e.shiftKey; };
        window.addEventListener('mousedown', update, true);
        window.addEventListener('keydown', update);
        window.addEventListener('keyup', update);
        return () => {
            window.removeEventListener('mousedown', update, true);
            window.removeEventListener('keydown', update);
            window.removeEventListener('keyup', update);
        };
    }, []);

    // Sort columns. `accessor` returns the comparison value; `numeric` uses
    // chronological ordering for timestamps.
    const SORT_COLUMNS = useMemo(() => ({
        scope: {
            label: 'Scope',
            accessor: n => (n.scope === 'order' || n.order_name || n.bulk_group_id)
                ? 'Order'
                : (n.team_name ? 'Team' : 'Club'),
        },
        store: { label: 'Store', accessor: n => STORE_DISPLAY_NAMES[n.store_key] || n.store_key || '' },
        location: { label: 'Location', accessor: n => (n.bulk_loc_count > 1 ? `Multiple Locations (${n.bulk_loc_count})` : (n.location_name || '')) },
        target: {
            label: 'Club / Team / Order',
            accessor: n => n.bulk_group_id
                ? `Multiple Orders (${n.bulk_order_count})`
                : (n.order_name
                    || (n.team_name ? `${n.club_name || ''} · ${n.team_name}` : (n.club_name || ''))
                    || ''),
        },
        // Fulfillment = OpsHub effective: 'Fulfilled' (single order closed OR
        // every order in the bulk group closed), 'Open' (anything still open),
        // '—' for Club-scope rows where the concept doesn't apply. Sort
        // accessor mirrors the visible label.
        fulfillment: { label: 'Fulfillment', accessor: n => n.is_fulfilled === true ? 'Fulfilled' : n.is_fulfilled === false ? 'Open' : 'zzz' },
        message: { label: 'Message', accessor: n => n.message || '' },
        updated_at: { label: 'Updated', accessor: n => n.updated_at || '' },
        created_by_username: { label: 'Author', accessor: n => n.created_by_username || '' },
    }), []);

    const sortedList = useMemo(() => {
        const accessor = SORT_COLUMNS[sortKey]?.accessor || (() => '');
        const rows = [...list];
        rows.sort((a, b) => {
            const av = accessor(a);
            const bv = accessor(b);
            const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [list, sortKey, sortDir, SORT_COLUMNS]);

    // Apply fulfilled-mode filter on top of the sort. Highlight mode keeps
    // every row; Hide mode drops fulfilled rows; Show is a passthrough.
    const visibleList = useMemo(() => (
        fulfilledMode === 'hide' ? sortedList.filter(n => n.is_fulfilled !== true) : sortedList
    ), [sortedList, fulfilledMode]);
    const hiddenCount = list.length - visibleList.length;

    // Selection helpers
    const visibleIds = useMemo(() => visibleList.filter(n => !n.is_system_group).map(n => n.id), [visibleList]);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    const someVisibleSelected = !allVisibleSelected && visibleIds.some(id => selectedIds.has(id));

    const onToggleSelectAll = () => {
        const next = new Set(selectedIds);
        if (allVisibleSelected) visibleIds.forEach(id => next.delete(id));
        else visibleIds.forEach(id => next.add(id));
        setSelectedIds(next);
        setLastClickedId(null);
    };

    const onToggleRow = (id) => {
        const willCheck = !selectedIds.has(id);
        if (shiftHeldRef.current && lastClickedId !== null && lastClickedId !== id) {
            const idxA = visibleIds.indexOf(lastClickedId);
            const idxB = visibleIds.indexOf(id);
            if (idxA !== -1 && idxB !== -1) {
                const [lo, hi] = idxA < idxB ? [idxA, idxB] : [idxB, idxA];
                const next = new Set(selectedIds);
                for (let i = lo; i <= hi; i++) {
                    if (willCheck) next.add(visibleIds[i]);
                    else next.delete(visibleIds[i]);
                }
                setSelectedIds(next);
                setLastClickedId(id);
                return;
            }
        }
        const next = new Set(selectedIds);
        if (willCheck) next.add(id); else next.delete(id);
        setSelectedIds(next);
        setLastClickedId(id);
    };

    // Bulk-delete confirmation. Built from the current selection at the moment
    // the button is clicked so the modal can render the blast radius even if
    // a fetch refresh races.
    const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null);
    const selectedRows = useMemo(() => visibleList.filter(n => selectedIds.has(n.id)), [visibleList, selectedIds]);
    const selectedOrderCount = useMemo(() => selectedRows.reduce((s, n) => s + (n.bulk_group_id ? (n.bulk_order_count || 1) : 1), 0), [selectedRows]);
    const selectedHasBulk = selectedRows.some(n => !!n.bulk_group_id);

    const onBulkDeleteClick = () => {
        if (selectedRows.length === 0) return;
        setBulkDeleteConfirm({
            rows: selectedRows,
            rowCount: selectedRows.length,
            orderCount: selectedOrderCount,
            hasBulk: selectedHasBulk,
        });
    };
    const confirmBulkDelete = async () => {
        if (!bulkDeleteConfirm) return;
        setBulkDeleteConfirm(null);
        try {
            await Promise.allSettled(bulkDeleteConfirm.rows.map(n =>
                n.bulk_group_id
                    ? fetchAuth(`/api/settings/notifications/bulk/${n.bulk_group_id}`, { method: 'DELETE' })
                    : fetchAuth(`/api/settings/notifications/${n.id}`, { method: 'DELETE' })
            ));
        } catch (e) {
            setError(String(e?.message || e));
        }
        setSelectedIds(new Set());
        setLastClickedId(null);
        refresh();
    };

    const onSortClick = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir(key === 'updated_at' ? 'desc' : 'asc'); }
    };
    const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    // Stores the user has access to. Admins see all stores; everyone else is
    // limited to the stores their assigned locations live in.
    const userStores = useMemo(() => {
        if (isAdmin) return STORE_KEYS;
        const s = new Set();
        for (const l of allLocations) { if (l.store_key) s.add(l.store_key); }
        return STORE_KEYS.filter(sk => s.has(sk));
    }, [isAdmin, allLocations]);
    const isSingleStore = userStores.length === 1;

    // Locations the user has access to per store. Used to narrow the
    // location dropdown for non-admins (admins see every active location).
    const userSlocsByStore = useMemo(() => {
        const m = {};
        for (const l of allLocations) {
            if (!l.store_key || !l.shopify_location_id) continue;
            (m[l.store_key] = m[l.store_key] || new Set()).add(String(l.shopify_location_id));
        }
        return m;
    }, [allLocations]);

    const franchiseStoreKey = useMemo(() => {
        if (!isFranchise) return '';
        const sk = (allLocations.find(l => l.store_key) || {}).store_key;
        return sk || STORE_KEYS[0];
    }, [isFranchise, allLocations]);

    // Scope selector — 'club' or 'order'
    const [scope, setScope] = useState('club');

    // Editing existing notification — when set, scope/store/loc/club/order
    // fields are locked and only the message can be changed. Save will upsert
    // (server already handles ON CONFLICT). To change scope/target the user
    // must delete and recreate.
    const [editingId, setEditingId] = useState(null);
    // When set, "edit" is targeting a bulk-group notification (N orders share
    // one message). Save calls PATCH /bulk/{group_id}; the composer suppresses
    // the scope/target fields and just shows "Editing N orders".
    const [editingBulkGroup, setEditingBulkGroup] = useState(null);
    // { id, group_id, count, message } — populated from handleEdit on a bulk row.

    // Delete confirmation for bulk groups — single-row deletes still fire
    // straight through DELETE /api/settings/notifications/{id}.
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    // { kind: 'bulk' | 'single', target: <group_id or row id>, count, label }

    // Multi-order expand popover for the "Multiple Orders (N)" cell.
    const [expandedGroup, setExpandedGroup] = useState(null);
    // { group_id, anchor: DOMRect, orders: [{order_name, ...}] | null (=loading) }

    // Shared
    const [storeKey, setStoreKey] = useState(franchiseStoreKey || (userStores.length === 1 ? userStores[0] : ''));
    const [message, setMessage] = useState('');
    const [saving, setSaving] = useState(false);

    // Auto-default the store when the user only has access to one. Also
    // re-runs when userStores changes (e.g. user object loads after first
    // render). The dropdown is hidden in single-store mode — see render.
    useEffect(() => {
        if (isSingleStore && !storeKey) {
            setStoreKey(userStores[0]);
        }
    }, [isSingleStore, userStores, storeKey]);

    // Club-mode state
    const [sloc, setSloc] = useState('');
    const [storeLocations, setStoreLocations] = useState([]);
    const [locsLoading, setLocsLoading] = useState(false);
    const [clubs, setClubs] = useState([]);
    const [clubsLoading, setClubsLoading] = useState(false);
    const [selectedClub, setSelectedClub] = useState('');
    // Team narrowing (2026-07-09): optional dropdown that appears once a
    // club is selected. Empty string = "All teams" = club-scope notification.
    const [teams, setTeams] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState('');

    // Order-mode state
    const [orderQuery, setOrderQuery] = useState('');
    const [orderLookup, setOrderLookup] = useState(null);   // resolved order or null
    const [orderLookupErr, setOrderLookupErr] = useState(null);
    const [orderLooking, setOrderLooking] = useState(false);

    const refresh = useCallback(() => {
        setLoading(true);
        fetchAuth('/api/settings/notifications')
            .then(r => r.json())
            .then(d => setList(d.notifications || []))
            .catch(e => setError(String(e)))
            .finally(() => setLoading(false));
    }, [fetchAuth]);

    useEffect(() => { refresh(); }, [refresh]);

    // Fetch locations for the picked store.
    // For non-admins, /api/locations 403s (site_manager / franchise / user
    // / location can't list everything), so skip the fetch entirely and
    // use the locations the user is already assigned to. Admin hits the
    // full endpoint and sees every active location.
    useEffect(() => {
        if (!storeKey) { setStoreLocations([]); setSloc(''); return; }
        if (!isAdmin) {
            const mine = allLocations
                .filter(l => l.store_key === storeKey && l.shopify_location_id && l.shopify_location_id !== '0')
                .map(l => ({
                    id:                   l.id,
                    name:                 l.name,
                    location_name:        l.name,
                    shopify_location_id:  String(l.shopify_location_id),
                    active:               true,
                }));
            setStoreLocations(mine);
            return;
        }
        setLocsLoading(true);
        fetchAuth(`/api/locations?store=${encodeURIComponent(storeKey)}`)
            .then(r => r.ok ? r.json() : [])
            .then(rows => {
                const active = (Array.isArray(rows) ? rows : [])
                    .filter(l => l && l.active && l.shopify_location_id && l.shopify_location_id !== '0');
                setStoreLocations(active);
            })
            .catch(() => setStoreLocations([]))
            .finally(() => setLocsLoading(false));
    }, [storeKey, fetchAuth, isAdmin, allLocations]);

    // Auto-default the location when the user has exactly one in this store
    // (e.g. franchise + single-store + single-location collapses the whole
    // top of the form to just the club picker).
    useEffect(() => {
        if (!isAdmin && storeLocations.length === 1 && !sloc) {
            setSloc(String(storeLocations[0].shopify_location_id));
        }
    }, [isAdmin, storeLocations, sloc]);

    const locOptions = useMemo(() => ([
        { label: locsLoading ? 'Loading…' : (storeLocations.length === 0 ? '— No locations —' : '— Select location —'),
          value: '' },
        ...storeLocations.map(l => ({
            label: l.location_name || l.name || l.shopify_location_id,
            value: String(l.shopify_location_id),
        })),
    ]), [storeLocations, locsLoading]);

    // Clubs (club mode)
    useEffect(() => {
        if (scope !== 'club' || !storeKey || !sloc) { setClubs([]); setSelectedClub(''); return; }
        setClubsLoading(true);
        const qs = new URLSearchParams({ store_key: storeKey, shopify_location_id: sloc });
        fetchAuth(`/api/settings/notifications/clubs?${qs}`)
            .then(r => r.json())
            .then(d => setClubs(d.clubs || []))
            .catch(() => setClubs([]))
            .finally(() => setClubsLoading(false));
    }, [scope, storeKey, sloc, fetchAuth]);

    // Teams (team narrowing) — populates only when a club is selected.
    useEffect(() => {
        if (scope !== 'club' || !storeKey || !selectedClub) {
            setTeams([]); setSelectedTeam(''); return;
        }
        setTeamsLoading(true);
        const qs = new URLSearchParams({ store_key: storeKey, club_name: selectedClub });
        fetchAuth(`/api/settings/notifications/teams?${qs}`)
            .then(r => r.json())
            .then(d => setTeams(d.teams || []))
            .catch(() => setTeams([]))
            .finally(() => setTeamsLoading(false));
    }, [scope, storeKey, selectedClub, fetchAuth]);

    // Reset order-side state when toggling away from order mode
    useEffect(() => {
        if (scope !== 'order') {
            setOrderQuery('');
            setOrderLookup(null);
            setOrderLookupErr(null);
        } else {
            setSloc('');
            setSelectedClub('');
            setSelectedTeam('');
        }
    }, [scope]);

    // Order lookup — fires on blur or Enter
    const doOrderLookup = useCallback(async () => {
        const q = (orderQuery || '').trim();
        setOrderLookup(null);
        setOrderLookupErr(null);
        if (!q || !storeKey) return;
        setOrderLooking(true);
        try {
            const qs = new URLSearchParams({ store_key: storeKey, q });
            const r = await fetchAuth(`/api/settings/notifications/order-lookup?${qs}`);
            const d = await r.json().catch(() => ({}));
            if (!r.ok) {
                setOrderLookupErr(d.detail || `HTTP ${r.status}`);
            } else {
                setOrderLookup(d);
            }
        } catch (e) {
            setOrderLookupErr(String(e.message || e));
        } finally {
            setOrderLooking(false);
        }
    }, [orderQuery, storeKey, fetchAuth]);

    const charsLeft = 300 - message.length;
    const messageOk = message.trim().length > 0 && message.length <= 300;
    const canSave = !saving && messageOk && !!storeKey && (
        editingBulkGroup
            ? true  // bulk-edit: only message changes; scope/target locked to the group
            : scope === 'club'
                ? (!!sloc && !!selectedClub)
                : (!!orderLookup && !!orderLookup.order_id)
    );

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            // Bulk-group edit takes a different endpoint (one message → N orders).
            if (editingBulkGroup) {
                const r = await fetchAuth(
                    `/api/settings/notifications/bulk/${editingBulkGroup.group_id}`,
                    {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ message: message.trim() }),
                    }
                );
                if (!r.ok) {
                    const d = await r.json().catch(() => ({}));
                    throw new Error(d.detail || `HTTP ${r.status}`);
                }
                handleCancelEdit();
                refresh();
                return;
            }
            const body = {
                store_key: storeKey,
                message: message.trim(),
            };
            if (editingId) {
                // Edit-by-id: backend updates that row's message in place;
                // scope/target are derived from the row, not the body.
                body.id = editingId;
            } else if (scope === 'club') {
                body.shopify_location_id = sloc;
                body.club_name = selectedClub;
                if (selectedTeam) body.team_name = selectedTeam;
            } else {
                body.order_id = orderLookup.order_id;
                body.order_name = orderLookup.order_name;
            }
            const r = await fetchAuth('/api/settings/notifications', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d.detail || `HTTP ${r.status}`);
            }
            setMessage('');
            setSelectedClub('');
            setSelectedTeam('');
            setOrderQuery('');
            setOrderLookup(null);
            setEditingId(null);
            refresh();
        } catch (e) {
            setError(String(e.message || e));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const r = await fetchAuth(`/api/settings/notifications/${id}`, { method: 'DELETE' });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            if (editingId === id) handleCancelEdit();
            refresh();
        } catch (e) {
            setError(String(e.message || e));
        }
    };

    // Bulk-delete: removes the message from ALL orders in the group. Only
    // invoked after the confirmation modal acknowledges the blast radius.
    const handleDeleteBulk = async (group_id) => {
        try {
            const r = await fetchAuth(
                `/api/settings/notifications/bulk/${group_id}`,
                { method: 'DELETE' }
            );
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            if (editingBulkGroup?.group_id === group_id) handleCancelEdit();
            refresh();
        } catch (e) {
            setError(String(e.message || e));
        }
    };

    const handleEdit = (n) => {
        setError(null);
        // Bulk-group edit: composer locks to the group, only message changes.
        if (n.bulk_group_id) {
            setEditingId(null);
            setEditingBulkGroup({
                group_id: n.bulk_group_id,
                count: n.bulk_order_count || 0,
                store_key: n.store_key,
            });
            setStoreKey(n.store_key);
            setMessage(n.message || '');
            if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setEditingId(n.id);
        setEditingBulkGroup(null);
        setStoreKey(n.store_key);
        setMessage(n.message || '');
        const isOrder = n.scope === 'order' || !!n.order_id;
        if (isOrder) {
            setScope('order');
            setOrderQuery(n.order_name || '');
            setOrderLookup({
                order_id: n.order_id,
                order_name: n.order_name,
                shopify_location_id: n.shopify_location_id,
                location_name: n.location_name,
                club_name: n.club_name,
                cancelled: false,
            });
            setOrderLookupErr(null);
        } else {
            setScope('club');
            setSloc(n.shopify_location_id || '');
            setSelectedClub(n.club_name || '');
            setSelectedTeam(n.team_name || '');
        }
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingBulkGroup(null);
        setMessage('');
        setSelectedClub('');
        setSelectedTeam('');
        setOrderQuery('');
        setOrderLookup(null);
        setOrderLookupErr(null);
    };

    // Fetch the order list for a bulk group when the user clicks the
    // "Multiple Orders (N)" cell. Renders a small popover.
    // Also handles system-group rows (is_system_group === true) by dispatching
    // to the /system/{kind}/orders endpoint instead.
    const expandGroup = async (n, anchorEl) => {
        const rect = anchorEl?.getBoundingClientRect?.();
        const isSystemGroup = n.is_system_group === true;
        const popoverId = isSystemGroup
            ? `system:${n.store_key}:${n.system_kind}`
            : n.bulk_group_id;
        const count = isSystemGroup ? (n.system_order_count || 0) : (n.bulk_order_count || 0);
        setExpandedGroup({
            group_id: popoverId,
            count,
            store_key: n.store_key,
            is_system_group: isSystemGroup,
            system_kind: n.system_kind,
            anchorTop: rect ? rect.bottom + window.scrollY : 0,
            anchorLeft: rect ? rect.left + window.scrollX : 0,
            orders: null,
        });
        try {
            let r, d;
            if (isSystemGroup) {
                r = await fetchAuth(`/api/settings/notifications/system/${n.system_kind}/orders?store_key=${encodeURIComponent(n.store_key)}`);
                if (!r.ok) {
                    let detail = `${r.status}`;
                    try { detail = (await r.json())?.detail || detail; } catch {}
                    throw new Error(`Failed to load edited orders: ${detail}`);
                }
                d = await r.json();
            } else {
                r = await fetchAuth(`/api/settings/notifications/bulk/${n.bulk_group_id}/orders`);
                d = await r.json();
            }
            setExpandedGroup(prev => prev && prev.group_id === popoverId
                ? { ...prev, orders: d.orders || [] }
                : prev);
        } catch (e) {
            setExpandedGroup(prev => prev && prev.group_id === popoverId
                ? { ...prev, orders: [], error: String(e.message || e) }
                : prev);
        }
    };

    return (
        <div className="notifications-tab" style={{ padding: 16 }}>
            <BlockStack gap="400">
                {error && (
                    <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
                )}

                <div ref={composerRef} className="notifications-tab__composer">
                <Card>
                    <BlockStack gap="300">
                        <Text variant="headingSm" as="h3">
                            {editingBulkGroup
                                ? `Edit Bulk Notification (${editingBulkGroup.count} orders)`
                                : (editingId ? 'Edit Notification' : 'New Notification')}
                        </Text>

                        {editingBulkGroup && (
                            <Banner tone="info" onDismiss={handleCancelEdit}>
                                Editing the message for all {editingBulkGroup.count} orders in this bulk notification.
                                The new message will replace the current one across every order.
                            </Banner>
                        )}

                        {editingId && !editingBulkGroup && (
                            <Banner tone="info" onDismiss={handleCancelEdit}>
                                Editing existing notification. Update the message and click Save, or Cancel to start over.
                            </Banner>
                        )}

                        {!editingBulkGroup && (<>
                        <ChoiceList
                            title="Scope"
                            choices={[
                                { label: 'Club — every order for a club at a location', value: 'club', disabled: !!editingId },
                                { label: 'Order — one specific order',                   value: 'order', disabled: !!editingId },
                            ]}
                            selected={[scope]}
                            onChange={(vals) => setScope(vals[0] || 'club')}
                            disabled={!!editingId}
                        />

                        {scope === 'club' ? (
                            (() => {
                                // Hide the Location dropdown when the user has only one location
                                // in the currently-selected store — auto-select already picked it.
                                // Surfaces the "no extra taps" rule for franchise/single-location users.
                                const singleLocInThisStore = !isAdmin && storeLocations.length === 1;
                                const visibleCount = 1 + (!isSingleStore ? 1 : 0) + (!singleLocInThisStore ? 1 : 0);
                                const gridCols = visibleCount === 3 ? '1fr 1fr 1fr' : '1fr 1fr';
                                return (
                            <>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: gridCols,
                                gap: 12,
                            }}>
                                {!isSingleStore && (
                                    <Select
                                        label="Store"
                                        options={[
                                            { label: '— Select store —', value: '' },
                                            ...userStores.map(sk => ({ label: STORE_DISPLAY_NAMES[sk] || sk, value: sk })),
                                        ]}
                                        value={storeKey}
                                        disabled={isFranchise || !!editingId}
                                        onChange={v => { setStoreKey(v); setSloc(''); setSelectedClub(''); setSelectedTeam(''); }}
                                    />
                                )}
                                {!singleLocInThisStore && (
                                <Select
                                    label="Location"
                                    options={locOptions}
                                    value={sloc}
                                    disabled={!!editingId || !storeKey || locsLoading || storeLocations.length === 0}
                                    onChange={v => { setSloc(v); setSelectedClub(''); setSelectedTeam(''); }}
                                />
                                )}
                                <Select
                                    label="Club"
                                    options={[
                                        { label: clubsLoading
                                                ? 'Loading…'
                                                : (clubs.length === 0
                                                    ? 'No clubs found for this location'
                                                    : '— Select club —'),
                                          value: '' },
                                        ...clubs.map(c => ({ label: c, value: c })),
                                        ...(selectedClub && !clubs.includes(selectedClub)
                                            ? [{ label: selectedClub, value: selectedClub }]
                                            : []),
                                    ]}
                                    value={selectedClub}
                                    disabled={!!editingId || !sloc || clubsLoading || clubs.length === 0}
                                    onChange={v => { setSelectedClub(v); setSelectedTeam(''); }}
                                />
                            </div>
                            {/* Team narrowing (2026-07-09) — appears when a club is picked.
                                Leaving as "All teams (default)" keeps this a club-scope
                                notification. Picking a team narrows it to that team only. */}
                            {selectedClub && (
                                <div style={{ width: '30%' }}>
                                    <Select
                                        label="Team (optional)"
                                        options={[
                                            { label: teamsLoading
                                                    ? 'Loading…'
                                                    : (teams.length === 0
                                                        ? 'No teams found for this club'
                                                        : 'All teams (default)'),
                                              value: '' },
                                            ...teams.map(t => ({ label: t, value: t })),
                                            ...(selectedTeam && !teams.includes(selectedTeam)
                                                ? [{ label: selectedTeam, value: selectedTeam }]
                                                : []),
                                        ]}
                                        value={selectedTeam}
                                        disabled={!!editingId || teamsLoading || teams.length === 0}
                                        onChange={setSelectedTeam}
                                    />
                                </div>
                            )}
                            </>
                                );
                            })()
                        ) : (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isSingleStore ? '1fr' : '1fr 2fr',
                                gap: 12,
                            }}>
                                {!isSingleStore && (
                                <Select
                                    label="Store"
                                    options={[
                                        { label: '— Select store —', value: '' },
                                        ...userStores.map(sk => ({ label: STORE_DISPLAY_NAMES[sk] || sk, value: sk })),
                                    ]}
                                    value={storeKey}
                                    disabled={isFranchise || !!editingId}
                                    onChange={v => { setStoreKey(v); setOrderLookup(null); setOrderLookupErr(null); }}
                                />
                                )}
                                <TextField
                                    label="Order # (e.g., SP-12345)"
                                    value={orderQuery}
                                    onChange={v => { setOrderQuery(v); setOrderLookup(null); setOrderLookupErr(null); }}
                                    onBlur={doOrderLookup}
                                    autoComplete="off"
                                    disabled={!!editingId}
                                    connectedRight={
                                        <Button onClick={doOrderLookup} loading={orderLooking} disabled={!!editingId || !storeKey || !orderQuery.trim()}>
                                            Look up
                                        </Button>
                                    }
                                    helpText={
                                        orderLookupErr ? <span style={{ color: '#c92a2a' }}>{orderLookupErr}</span>
                                        : orderLookup ? (
                                            <span>
                                                Will fire on <b>{orderLookup.order_name}</b>
                                                {orderLookup.location_name ? <> at <b>{orderLookup.location_name}</b></> : null}
                                                {orderLookup.club_name ? <> · club: {orderLookup.club_name}</> : null}
                                                {orderLookup.cancelled ? <> · <span style={{ color: '#c92a2a' }}>CANCELLED</span></> : null}
                                            </span>
                                        ) : 'Type the order number then press Tab or Look up.'
                                    }
                                />
                            </div>
                        )}
                        </>)}

                        <TextField
                            label={`Notification message (${charsLeft} chars left)`}
                            value={message}
                            onChange={v => setMessage(v.slice(0, 300))}
                            multiline={3}
                            maxLength={300}
                            autoComplete="off"
                            error={message.length > 300 ? 'Too long' : undefined}
                        />
                        <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            {(editingId || editingBulkGroup) && (
                                <Button onClick={handleCancelEdit} disabled={saving}>Cancel</Button>
                            )}
                            <Button
                                variant="primary"
                                disabled={!canSave}
                                loading={saving}
                                onClick={handleSave}
                            >
                                {editingBulkGroup
                                    ? `Update ${editingBulkGroup.count} Notifications`
                                    : (editingId ? 'Update Notification' : 'Save Notification')}
                            </Button>
                        </div>
                    </BlockStack>
                </Card>
                </div>

                <Card>
                    <BlockStack gap="300">
                        <div ref={activeHeaderRef} className="notifications-tab__active-header">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                                    <Text variant="headingSm" as="h3">Active Notifications</Text>
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                        <Text as="span" tone="subdued" variant="bodySm">Fulfilled orders:</Text>
                                        <ButtonGroup variant="segmented">
                                            <Button size="slim" pressed={fulfilledMode === 'show'}      onClick={() => setFulfilledMode('show')}>Show</Button>
                                            <Button size="slim" pressed={fulfilledMode === 'hide'}      onClick={() => setFulfilledMode('hide')}>Hide</Button>
                                            <Button size="slim" pressed={fulfilledMode === 'highlight'} onClick={() => setFulfilledMode('highlight')}>Highlight</Button>
                                        </ButtonGroup>
                                        {fulfilledMode === 'hide' && hiddenCount > 0 && (
                                            <Text as="span" tone="subdued" variant="bodySm">({hiddenCount} hidden)</Text>
                                        )}
                                    </div>
                                </div>
                                {selectedRows.length > 0 && (
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                                        <Text as="span" tone="subdued" variant="bodySm">
                                            {selectedRows.length} selected
                                            {selectedHasBulk && selectedOrderCount !== selectedRows.length
                                                ? ` (${selectedOrderCount} orders)` : ''}
                                        </Text>
                                        <Button tone="critical" variant="primary" size="slim" onClick={onBulkDeleteClick}>
                                            Delete selected
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {loading ? (
                            <Text as="p" tone="subdued">Loading…</Text>
                        ) : visibleList.length === 0 ? (
                            <EmptyState heading={list.length === 0 ? "No active notifications" : "All notifications hidden"} image="">
                                <p>{list.length === 0
                                    ? 'Create one above to broadcast to operators.'
                                    : 'Every notification matches your filter. Uncheck "Hide fulfilled orders" to see them.'}</p>
                            </EmptyState>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                            <table className="data-table notifications-tab__table" style={{ width: '100%', minWidth: 760 }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: 36 }}>
                                            <input
                                                type="checkbox"
                                                aria-label={allVisibleSelected ? 'Clear all' : 'Select all'}
                                                checked={allVisibleSelected}
                                                ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                                                onChange={onToggleSelectAll}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </th>
                                        {Object.entries(SORT_COLUMNS).map(([key, col]) => (
                                            <th key={key}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => onSortClick(key)}>
                                                {col.label}{sortArrow(key)}
                                            </th>
                                        ))}
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleList.map(n => {
                                        const isBulk = !!n.bulk_group_id;
                                        const isSystemGroup = n.is_system_group === true;
                                        const isOrder = n.scope === 'order' || !!n.order_name;
                                        const target = isSystemGroup
                                            ? `Edited Orders (${n.system_order_count || 0})`
                                            : isBulk
                                            ? `Multiple Orders (${n.bulk_order_count})`
                                            : isOrder
                                                ? `Order ${n.order_name || n.order_id}`
                                                : (n.club_name || '');
                                        // Build link target — single Order rows link straight to the
                                        // order detail; Club rows link to the orders list pre-filtered
                                        // by the club + location combo. Bulk rows and system-group rows
                                        // keep the expand-group popover (no targetHref).
                                        let targetHref = '';
                                        if (!isBulk && !isSystemGroup && n.store_key) {
                                            if (isOrder && n.order_id) {
                                                targetHref = `/store/${n.store_key}/orders/${n.order_id}`;
                                            } else if (!isOrder && n.club_name) {
                                                const params = new URLSearchParams();
                                                params.append('clubs', n.club_name);
                                                if (n.shopify_location_id) {
                                                    params.append('locations', String(n.shopify_location_id));
                                                }
                                                targetHref = `/store/${n.store_key}/orders?${params.toString()}`;
                                            }
                                        }
                                        const highlight = fulfilledMode === 'highlight' && n.is_fulfilled === true;
                                        const isSelected = selectedIds.has(n.id);
                                        const isSystem = n.is_system === true;
                                        return (
                                            <tr key={n.id}
                                                style={highlight ? { background: '#fef9c3' } : undefined}>
                                                <td style={{ width: 36 }}>
                                                    <input
                                                        type="checkbox"
                                                        aria-label={isSelected ? 'Deselect row' : 'Select row'}
                                                        checked={isSelected}
                                                        onChange={() => !isSystemGroup && onToggleRow(n.id)}
                                                        disabled={isSystemGroup}
                                                        style={{ cursor: isSystemGroup ? 'default' : 'pointer' }}
                                                    />
                                                </td>
                                                <td>{isOrder || isBulk || isSystemGroup ? 'Order' : 'Club'}</td>
                                                <td>{STORE_DISPLAY_NAMES[n.store_key] || n.store_key}</td>
                                                <td>{
                                                    isSystemGroup
                                                        ? 'All Locations'
                                                        : isBulk
                                                        ? (n.bulk_loc_count > 1
                                                            ? `Multiple Locations (${n.bulk_loc_count})`
                                                            : (n.location_name || '—'))
                                                        : n.location_name
                                                }</td>
                                                <td>
                                                    {(isBulk || isSystemGroup) ? (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => expandGroup(n, e.currentTarget)}
                                                            style={{
                                                                background: 'none', border: 'none', padding: 0,
                                                                color: '#2c6ecb', cursor: 'pointer',
                                                                textDecoration: 'underline', font: 'inherit',
                                                            }}
                                                            title={isSystemGroup
                                                                ? "Click to see all edited (:A) orders for this store"
                                                                : "Click to see the orders in this group"}
                                                        >
                                                            {target}
                                                        </button>
                                                    ) : targetHref ? (
                                                        <a href={targetHref}
                                                           style={{ color: '#2c6ecb', textDecoration: 'underline' }}
                                                           title={isOrder
                                                               ? 'Open this order'
                                                               : 'Open the orders list filtered by this club and location'}>
                                                            {target}
                                                        </a>
                                                    ) : target}
                                                </td>
                                                <td>
                                                    {n.is_fulfilled === true && (
                                                        <span style={{
                                                            display: 'inline-block', padding: '2px 8px',
                                                            borderRadius: 10, fontSize: 11, fontWeight: 600,
                                                            letterSpacing: 0.2,
                                                            background: '#d1fae5', color: '#065f46',
                                                        }} title="All orders fulfilled per OpsHub rules">
                                                            Fulfilled
                                                        </span>
                                                    )}
                                                    {n.is_fulfilled === false && (
                                                        <span style={{
                                                            display: 'inline-block', padding: '2px 8px',
                                                            borderRadius: 10, fontSize: 11, fontWeight: 600,
                                                            letterSpacing: 0.2,
                                                            background: '#dbeafe', color: '#1e3a8a',
                                                        }} title={isBulk ? "At least one order still open per OpsHub rules" : "Order still open per OpsHub rules"}>
                                                            Open
                                                        </span>
                                                    )}
                                                    {n.is_fulfilled == null && (
                                                        <span style={{ color: '#6d7175' }} title="Fulfillment status doesn't apply to club-scope notifications">—</span>
                                                    )}
                                                </td>
                                                <td
                                                    title={n.message}
                                                    style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                >
                                                    {isSystem && (
                                                        <span title="System notification" aria-label="System notification" style={{ marginRight: 6 }}>🔒</span>
                                                    )}
                                                    {n.message}
                                                </td>
                                                <td>{n.updated_at ? new Date(n.updated_at).toLocaleString() : ''}</td>
                                                <td>{n.created_by_username || ''}</td>
                                                <td>
                                                    {isSystem ? (
                                                        <Tooltip content="Auto-generated for edited orders — cannot be cleared.">
                                                            <div style={{ display: 'inline-flex', gap: 6 }}>
                                                                <Button size="slim" disabled>Edit</Button>
                                                                <Button tone="critical" variant="primary" size="slim" disabled>Delete</Button>
                                                            </div>
                                                        </Tooltip>
                                                    ) : (
                                                        <div style={{ display: 'inline-flex', gap: 6 }}>
                                                            <Button
                                                                size="slim"
                                                                onClick={() => handleEdit(n)}
                                                                disabled={
                                                                    isBulk
                                                                        ? editingBulkGroup?.group_id === n.bulk_group_id
                                                                        : editingId === n.id
                                                                }
                                                            >
                                                                {(isBulk
                                                                    ? editingBulkGroup?.group_id === n.bulk_group_id
                                                                    : editingId === n.id)
                                                                    ? 'Editing…' : 'Edit'}
                                                            </Button>
                                                            <Button
                                                                tone="critical"
                                                                variant="primary"
                                                                size="slim"
                                                                onClick={() => {
                                                                    if (isBulk) {
                                                                        setDeleteConfirm({
                                                                            kind: 'bulk',
                                                                            target: n.bulk_group_id,
                                                                            count: n.bulk_order_count || 0,
                                                                            label: `Multiple Orders (${n.bulk_order_count || 0})`,
                                                                        });
                                                                    } else {
                                                                        handleDelete(n.id);
                                                                    }
                                                                }}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </BlockStack>
                </Card>
            </BlockStack>

            {/* Bulk-delete confirmation modal — guards the blast radius of
                removing a notification from all N orders at once. Single-row
                deletes (non-bulk) still go through directly. */}
            <Modal
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Remove bulk notification?"
                primaryAction={{
                    content: `Remove from ${deleteConfirm?.count || 0} orders`,
                    destructive: true,
                    onAction: async () => {
                        if (deleteConfirm?.kind === 'bulk') {
                            await handleDeleteBulk(deleteConfirm.target);
                        }
                        setDeleteConfirm(null);
                    },
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: () => setDeleteConfirm(null),
                }]}
            >
                <Modal.Section>
                    <Text as="p">
                        This will remove the notification from all <b>{deleteConfirm?.count || 0}</b> orders
                        in this group. The notification will disappear from each order's detail page and
                        from the Orders list. This cannot be undone.
                    </Text>
                    <Text as="p" tone="subdued">
                        To remove the notification from a single order only, open that order and delete it from there.
                    </Text>
                </Modal.Section>
            </Modal>

            {/* Bulk multi-row delete confirmation — shows the blast radius
                when the selection contains bulk groups (each bulk row deletes
                every notification in that group). */}
            <Modal
                open={!!bulkDeleteConfirm}
                onClose={() => setBulkDeleteConfirm(null)}
                title={`Delete ${bulkDeleteConfirm?.rowCount || 0} notification${bulkDeleteConfirm?.rowCount === 1 ? '' : 's'}?`}
                primaryAction={{
                    content: bulkDeleteConfirm?.hasBulk
                        ? `Remove from ${bulkDeleteConfirm?.orderCount || 0} orders`
                        : `Delete ${bulkDeleteConfirm?.rowCount || 0}`,
                    destructive: true,
                    onAction: confirmBulkDelete,
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: () => setBulkDeleteConfirm(null),
                }]}
            >
                <Modal.Section>
                    <Text as="p">
                        This will delete <b>{bulkDeleteConfirm?.rowCount || 0}</b> selected
                        notification{bulkDeleteConfirm?.rowCount === 1 ? '' : 's'}.
                        {bulkDeleteConfirm?.hasBulk && bulkDeleteConfirm?.orderCount !== bulkDeleteConfirm?.rowCount && (
                            <>
                                {' '}Because some selected rows are bulk groups, this removes the notification from
                                a total of <b>{bulkDeleteConfirm.orderCount}</b> orders. This cannot be undone.
                            </>
                        )}
                        {!bulkDeleteConfirm?.hasBulk && ' This cannot be undone.'}
                    </Text>
                </Modal.Section>
            </Modal>

            {/* Multi-order expand popover — anchored under the
                "Multiple Orders (N)" / "Edited Orders (N)" cell. Lists the
                orders in the group so the user can verify scope.
                System-group rows show richer info (club, updated_at, message). */}
            {expandedGroup && (
                <>
                    <div
                        onClick={() => setExpandedGroup(null)}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 999,
                            background: 'transparent',
                        }}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            top: expandedGroup.anchorTop + 4,
                            left: expandedGroup.anchorLeft,
                            zIndex: 1000,
                            background: '#fff',
                            border: '1px solid #c9cccf',
                            borderRadius: 6,
                            boxShadow: '0 4px 16px rgba(0,0,0,.12)',
                            padding: 12,
                            minWidth: expandedGroup.is_system_group ? 380 : 240,
                            maxHeight: 380,
                            overflowY: 'auto',
                        }}
                    >
                        <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 6 }}>
                            {expandedGroup.is_system_group
                                ? `${expandedGroup.count} edited (:A) orders`
                                : `${expandedGroup.count} orders in this group`}
                        </div>
                        {expandedGroup.orders === null ? (
                            <Text as="p" tone="subdued">Loading…</Text>
                        ) : expandedGroup.orders.length === 0 ? (
                            <Text as="p" tone="subdued">{expandedGroup.error || 'No orders found.'}</Text>
                        ) : expandedGroup.is_system_group ? (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13 }}>
                                {expandedGroup.orders.map(o => (
                                    <li key={o.id} style={{ padding: '4px 0', borderBottom: '1px solid #f1f2f3' }}>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                                            <a
                                                href={`/store/${o.store_key}/orders/${o.order_id}`}
                                                style={{ color: '#2c6ecb', textDecoration: 'none', fontWeight: 600, flexShrink: 0 }}
                                            >
                                                {o.order_name || o.order_id}
                                            </a>
                                            {o.club_name && (
                                                <span style={{ color: '#6d7175', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {o.club_name}
                                                </span>
                                            )}
                                            {o.updated_at && (
                                                <span style={{ color: '#adb0b3', fontSize: 11, flexShrink: 0, marginLeft: 'auto' }}>
                                                    {new Date(o.updated_at).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                        {o.message && (
                                            <div style={{ color: '#6d7175', fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {o.message}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13 }}>
                                {expandedGroup.orders.map(o => (
                                    <li key={o.order_id} style={{ padding: '3px 0' }}>
                                        <a
                                            href={`/store/${o.store_key}/orders/${o.order_id}`}
                                            style={{ color: '#2c6ecb', textDecoration: 'none' }}
                                        >
                                            {o.order_name || o.order_id}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
